// Browser modal for persisted long-form documents.
//
// Two-column layout:
//   - Left rail: list of saved documents (newest first), plus a "+ New from
//     template" header that expands the 4 template cards inline
//   - Right pane: viewer for the currently-selected document, OR the
//     template-picker explainer when no doc is selected
//
// Generation itself is handled by the existing GenerateModal — when the user
// picks a template here, this modal calls `onGenerate(template)` and the
// parent unmounts us, opens GenerateModal in stream mode, lands on save/discard,
// and (on save) re-opens us so the new doc is in the list.
//
// Stale provenance: each saved doc carries `source_snapshot: [{card_id, mtime}]`.
// We compare each entry's mtime to the live card's mtime — if any drifted,
// the doc row is marked "stale" and the viewer shows a "regenerate?" banner.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { api, DOC_TEMPLATES, type Card, type DocTemplate, type DocumentRecord } from '../api';

export interface DocumentsModalProps {
  projectId: string;
  /** Live cards array — used to compare snapshot mtimes for staleness. */
  cards: Card[];
  /** User picked a template card → parent runs the generate flow. */
  onGenerate: (template: DocTemplate) => void;
  onClose: () => void;
}

export function DocumentsModal({ projectId, cards, onGenerate, onClose }: DocumentsModalProps) {
  const { t } = useTranslation();
  const [docs, setDocs] = useState<DocumentRecord[] | null>(null);
  const [selected, setSelected] = useState<DocumentRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      const fresh = await api.listDocuments(projectId);
      setDocs(fresh);
      // Auto-show picker when there are no docs — empty list is uninviting.
      setShowPicker(fresh.length === 0);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const cardMtime = new Map(cards.map((c) => [c.card_id, c.mtime]));
  const isStale = (doc: DocumentRecord): boolean =>
    doc.source_snapshot.some((s) => {
      const live = cardMtime.get(s.card_id);
      // Card removed since generate → also stale.
      if (live === undefined) return true;
      return live !== s.mtime;
    });

  const remove = async (doc: DocumentRecord) => {
    if (!window.confirm(t('documents.confirmDelete', { title: doc.title }))) return;
    try {
      await api.deleteDocument(projectId, doc.id);
      if (selected?.id === doc.id) setSelected(null);
      await reload();
    } catch (e) {
      setError(t('documents.errDelete', { message: (e as Error).message }));
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(26, 24, 20, 0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
        padding: 24,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: 'white',
          borderRadius: 8,
          width: '92%',
          maxWidth: 1100,
          height: '88%',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '14px 20px',
            borderBottom: '1px solid var(--rule)',
            display: 'flex', alignItems: 'center', gap: 10,
          }}
        >
          <span className="serif" style={{ fontSize: 18, fontWeight: 500 }}>
            {t('documents.heading')}
          </span>
          <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: '.05em' }}>
            {docs ? t('documents.savedCount', { count: docs.length }) : '…'}
          </span>
          <span style={{ flex: 1 }} />
          <button
            onClick={onClose}
            className="mono"
            style={{
              fontSize: 12, color: 'var(--ink-3)',
              padding: '4px 10px', border: '1px solid var(--rule)', borderRadius: 4,
            }}
            title={t('common.close')}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          {/* Left rail */}
          <div
            style={{
              width: 320,
              borderRight: '1px solid var(--rule)',
              overflowY: 'auto',
              background: 'var(--paper-0)',
              flexShrink: 0,
            }}
          >
            <div style={{ padding: '12px 14px' }}>
              <button
                onClick={() => setShowPicker((v) => !v)}
                className="mono"
                style={{
                  width: '100%',
                  fontSize: 11,
                  padding: '8px 12px',
                  border: '1px solid var(--ink-0)',
                  background: showPicker ? 'var(--ink-0)' : 'white',
                  color: showPicker ? 'white' : 'var(--ink-0)',
                  fontWeight: 700,
                  letterSpacing: '.06em',
                  borderRadius: 4,
                  cursor: 'pointer',
                }}
              >
                {showPicker ? t('documents.templatesToggle') : t('documents.newFromTemplate')}
              </button>
            </div>

            {showPicker && (
              <div style={{ padding: '0 14px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {DOC_TEMPLATES.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => onGenerate(t.id)}
                    style={{
                      textAlign: 'left',
                      padding: '10px 12px',
                      border: '1px solid var(--rule)',
                      borderRadius: 4,
                      background: 'white',
                      cursor: 'pointer',
                    }}
                  >
                    <div className="mono" style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-0)', letterSpacing: '.04em' }}>
                      {t.label}
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--ink-2)', lineHeight: 1.4, marginTop: 3 }}>
                      {t.blurb}
                    </div>
                  </button>
                ))}
              </div>
            )}

            <div style={{ borderTop: '1px solid var(--rule)' }}>
              {loading && (
                <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', padding: '12px 14px' }}>
                  {t('common.loading')}
                </div>
              )}
              {error && (
                <div style={{ fontSize: 11.5, color: 'var(--red)', padding: '12px 14px' }}>{error}</div>
              )}
              {docs && docs.length === 0 && !loading && (
                <div style={{ fontSize: 11.5, color: 'var(--ink-3)', padding: '14px', fontStyle: 'italic' }}>
                  {t('documents.emptyList')}
                </div>
              )}
              {docs && docs.map((d) => {
                const stale = isStale(d);
                const isSelected = selected?.id === d.id;
                return (
                  <button
                    key={d.id}
                    onClick={() => setSelected(d)}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '10px 14px',
                      border: 'none',
                      borderBottom: '1px solid var(--rule)',
                      borderLeft: isSelected ? '3px solid var(--blue)' : '3px solid transparent',
                      background: isSelected ? 'var(--blue-soft)' : 'transparent',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                      <span className="mono" style={{ fontSize: 9.5, color: 'var(--ink-3)', letterSpacing: '.06em', fontWeight: 700 }}>
                        {d.template.toUpperCase()}
                      </span>
                      {stale && (
                        <span className="mono" style={{ fontSize: 9.5, color: 'var(--red)', fontWeight: 700, letterSpacing: '.04em' }}>
                          · {t('documents.stale')}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12.5, color: 'var(--ink-1)', marginTop: 2, lineHeight: 1.35 }}>
                      {d.title}
                    </div>
                    <div className="mono" style={{ fontSize: 9.5, color: 'var(--ink-3)', marginTop: 2 }}>
                      {new Date(d.generated_at).toLocaleString()}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right pane */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            {selected ? (
              <DocumentViewer
                doc={selected}
                stale={isStale(selected)}
                projectId={projectId}
                onRegenerate={() => onGenerate(selected.template as DocTemplate)}
                onDelete={() => remove(selected)}
              />
            ) : (
              <EmptyState />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--ink-3)',
        padding: 40,
        textAlign: 'center',
      }}
    >
      <div className="serif" style={{ fontSize: 22, color: 'var(--ink-2)', marginBottom: 10 }}>
        {t('documents.emptyTitle')}
      </div>
      <div style={{ fontSize: 13, lineHeight: 1.6, maxWidth: 420 }}>
        {t('documents.emptyBody')}
      </div>
    </div>
  );
}

function DocumentViewer({
  doc, stale, projectId, onRegenerate, onDelete,
}: {
  doc: DocumentRecord;
  stale: boolean;
  projectId: string;
  onRegenerate: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  // doc/12 §3.2.1: share = snapshot the markdown behind a public /s/
  // link and put it on the clipboard. 'copied' is transient UI feedback.
  const [shareState, setShareState] = useState<'idle' | 'busy' | 'copied'>('idle');
  const onShare = async () => {
    if (shareState === 'busy') return;
    setShareState('busy');
    try {
      const res = await api.createShare(projectId, 'document', doc.id);
      const url = `${window.location.origin}${res.url}`;
      await navigator.clipboard.writeText(url).catch(() => {});
      setShareState('copied');
      setTimeout(() => setShareState('idle'), 2000);
    } catch {
      setShareState('idle');
    }
  };
  return (
    <>
      <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--rule)', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span className="serif" style={{ fontSize: 16, fontWeight: 500, color: 'var(--ink-0)' }}>
          {doc.title}
        </span>
        <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: '.06em' }}>
          {doc.template} · {t('documents.cardCount', { count: doc.source_snapshot.length })} · {new Date(doc.generated_at).toLocaleString()}
        </span>
        <span style={{ flex: 1 }} />
        <button
          onClick={onShare}
          className="mono"
          style={{
            fontSize: 11, padding: '5px 12px',
            border: '1px solid var(--ink-0)', borderRadius: 4,
            background: 'var(--ink-0)', color: 'white', fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {shareState === 'copied'
            ? t('documents.shareCopied')
            : t('documents.share')}
        </button>
        <button
          onClick={onDelete}
          className="mono"
          style={{
            fontSize: 11, padding: '5px 10px',
            border: '1px solid var(--rule)', borderRadius: 4,
            background: 'white', color: 'var(--red)', fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {t('common.delete')}
        </button>
      </div>
      {stale && (
        <div
          style={{
            padding: '8px 20px',
            background: 'var(--amber-soft)',
            borderBottom: '1px solid var(--amber)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontSize: 12,
          }}
        >
          <span className="mono" style={{ color: 'var(--red)', fontWeight: 700, letterSpacing: '.06em', fontSize: 10 }}>
            ⚠ {t('documents.stale')}
          </span>
          <span style={{ color: 'var(--ink-1)' }}>
            {t('documents.staleBanner')}
          </span>
          <span style={{ flex: 1 }} />
          <button
            onClick={onRegenerate}
            className="mono"
            style={{
              fontSize: 11, padding: '5px 12px',
              border: '1px solid var(--ink-0)', borderRadius: 4,
              background: 'var(--ink-0)', color: 'white', fontWeight: 700,
              letterSpacing: '.04em', cursor: 'pointer',
            }}
          >
            {t('documents.regenerate')}
          </button>
        </div>
      )}
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 40px 40px', background: 'var(--paper-0)' }}>
        <article className="md-render">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{doc.markdown}</ReactMarkdown>
        </article>
      </div>
    </>
  );
}
